const hasForbiddenFirebaseKeyCharacter = (value) => (
  ['.', '#', '$', '/', '[', ']'].some((character) => value.includes(character))
);

/**
 * Keep one workforce identity from pre-onboarding through actual start.
 *
 * `employeeKey` is written when the ATS first creates the forecast employee.
 * `onboardingHireId` covers a retry/relaunch after formal onboarding already began.
 * A newly allocated key is only used when neither durable link is valid.
 */
export function selectOnboardingEmployeeId({
  allocatedEmployeeId,
  connectionData = {}
} = {}) {
  const candidates = [connectionData?.employeeKey, connectionData?.onboardingHireId];
  const linkedEmployeeId = candidates
    .map((value) => String(value || '').trim())
    .find((value) => value && !hasForbiddenFirebaseKeyCharacter(value));

  return linkedEmployeeId || String(allocatedEmployeeId || '').trim();
}

/**
 * Verify that a reused employee key still belongs to this ATS candidate.
 * An absent employee is safe because the selected key has not been claimed yet;
 * a populated employee must match either the durable connection or candidate ID.
 */
export function employeeMatchesOnboardingCandidate({
  employee = {},
  connectionKey = '',
  applicantId = ''
} = {}) {
  if (!employee || typeof employee !== 'object' || Object.keys(employee).length === 0) {
    return true;
  }

  const expectedConnectionKey = String(connectionKey || '').trim();
  const expectedApplicantId = String(applicantId || '').trim();
  const employeeConnectionKeys = [employee.connectionKey, employee?.meta?.connectionKey]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const employeeCandidateIds = [
    employee.applicantId,
    employee.candidateId,
    employee?.meta?.applicantId,
    employee?.meta?.candidateId
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  return Boolean(
    (expectedConnectionKey && employeeConnectionKeys.includes(expectedConnectionKey))
    || (expectedApplicantId && employeeCandidateIds.includes(expectedApplicantId))
  );
}

export default selectOnboardingEmployeeId;
